import React from 'react';
import PropTypes from 'prop-types';
import hooks from '@g4/automation-hooks';
import styled, { withTheme } from 'styled-components';

export const propTypes = {
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Determines where delimiters are placed */
  groupLengths: PropTypes.array.isRequired,
  /** Character string used to break up input value into subgroups */
  delimiter: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  withBorder: PropTypes.bool,
  value: PropTypes.string,
  passRef: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
};

export const defaultProps = {
  delimiter: '-',
  onChange: Function.prototype,
  onFocus: Function.prototype,
  onBlur: Function.prototype,
  withBorder: true,
};

/** @visibleName Masked Numeric Input */
class MaskedNumericInput extends React.Component {
  constructor(props) {
    super(props);

    this.convert = this.convert.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.toRef = this.toRef.bind(this);

    const value = this.convert(props.value || '');

    this.state = {
      value,
      beforeChange: value,
    };
  }

  static propTypes = propTypes;
  static defaultProps = defaultProps;

  get value() {
    return (
      this.state.value.replace(new RegExp(this.props.delimiter, 'g'), '') || ''
    );
  }
  toRef(el) {
    this.el = el;
    this.props.passRef && this.props.passRef(el);
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.value) {
      const value = this.convert(nextProps.value);

      this.setState({
        value,
      });
    }
  }

  // Todo: Backspace doesn't allow you to enter a previous group
  convert(value = '') {
    const { delimiter, groupLengths } = this.props;

    // Remove non-numeric characters
    const unformatted = value.replace(/[^0-9]+/g, '');

    var formatted = '';

    var start = 0;
    for (let i = 0; i < groupLengths.length; i++) {
      const groupLength = parseInt(groupLengths[i], 10);
      const chunk = unformatted.substring(start, start + groupLength);

      formatted += chunk;

      // Not last group
      if (i !== groupLengths.length - 1) {
        // Last character of group was entered
        if (chunk.length === groupLength) {
          // Insert delimiter character
          formatted += delimiter;
        }
      }

      start += groupLength;
    }

    if (formatted.charAt(formatted.length - 1) === delimiter) {
      let formattedWithoutLastDash = formatted.slice(0, formatted.length - 1);
      return formattedWithoutLastDash;
    }
    return formatted;
  }

  handleChange(event) {
    const { selectionStart } = this.el;
    const { delimiter } = this.props;
    const { beforeChange } = this.state;

    var value = event.target.value;
    var newSelectionStart = selectionStart;

    // Determine whether we're adding or removing characters by comparing the value's at
    // `onKeyDown` and `onChange`
    const backspace = beforeChange.length > value.length;
    const deleteBeyondGroup =
      backspace && beforeChange.charAt(selectionStart) === delimiter;

    // GMOB-732: Allow the backspace key to delete characters beyond the bounds of groups
    if (deleteBeyondGroup) {
      // Remove delimiter and last character of the group
      value =
        value.substring(0, selectionStart - 1) +
        value.substring(selectionStart);
    }

    const formatted = this.convert(value);

    if (
      !backspace &&
      (formatted.charAt(selectionStart) === delimiter ||
        formatted.charAt(selectionStart - 1) === delimiter)
    ) {
      // Update the caret position by accounting for the injected delimiter character
      newSelectionStart++;
    }

    this.setState({ value: formatted }, () => {
      this.props.onChange(this.value);

      // Without this, the caret will always reset to the end of the input
      // GMOB-773: `setTimeout` fixes Android not applying `setSelectionRange`
      setTimeout(() => {
        // GMOB2-99: The caret should always be right aligned of the delimiter
        this.el.setSelectionRange(newSelectionStart, newSelectionStart);
      });
    });
  }

  onKeyDown(event) {
    // Don't rely on `event.which` or `event.keyCode` since they're not detected correctly
    // on Android https://bugs.chromium.org/p/chromium/issues/detail?id=118639

    this.setState({
      beforeChange: this.state.value,
    });
  }

  handleBlur(event) {
    this.props.onBlur(event.target.value);
  }

  render() {
    return (
      <input
        {...this.attrs}
        ref={this.toRef}
        name={this.props.name}
        id={this.props.id}
        className={this.props.className}
        // GMOB2-227: Changed from type 'text' since otherwise Android doesnt correctly
        // read `HTMLInputElement.prototype.selectionStart`. One day the `inputmode` attribute
        // will be supported.
        type="tel"
        value={this.state.value}
        onChange={this.handleChange}
        onKeyDown={this.onKeyDown}
        onFocus={this.props.onFocus}
        onBlur={this.handleBlur}
        placeholder={this.props.placeholder}
        tabIndex="0"
        {...hooks.hook(this.props.automationHook)}
      />
    );
  }
}

const StyledMaskedInput = styled(MaskedNumericInput)`
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 ${props => props.theme.spacing(4)};
  border-radius: 8px;
  text-overflow: ellipsis;

  ${props =>
    props.disabled &&
    `
    color: ${props.theme.colors.input.disabledText};
    background-color: ${props.theme.colors.input.disabledBackground};
  `}

  &:hover,
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.input.focused};
  }

  ${props =>
    props.icon &&
    `
    /* avoid collision with icon */
    padding-right: 50px;
  `}

  ${props =>
    props.withBorder &&
    `
    border: 1px solid ${props.theme.colors.input.border};
  `}


  ${props =>
    props.hasError &&
    !props.disabled &&
    `
    border-color: ${props.theme.colors.input.error};
  `}

  ${props =>
    props.hideSpinControls &&
    `
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      appearance: none;
      margin: 0;
    }
  `}

  &::placeholder {
    color: ${props => props.theme.colors.input.placeholderText};
  }
`;

export default withTheme(StyledMaskedInput);
